import React, { useEffect, useRef, useState } from "react";
import "./RollingRate.scss";

const DIGIT_HEIGHT = 21;

const RollingRate = ({
  oldValue,
  newValue,
  prefix = "",
  suffix = "",
}) => {
  const [animate, setAnimate] = useState(false);
  const containerRef = useRef(null);

  // 🔥 trigger only when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setAnimate(true), 200); // slight delay feels natural
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const formatParts = (num) => {
    const [int, dec] = Number(num).toFixed(2).split(".");
    return { int, dec };
  };

  const oldParts = formatParts(oldValue);
  const newParts = formatParts(newValue);

  const maxIntLength = Math.max(
    oldParts.int.length,
    newParts.int.length
  );

  const oldInt = oldParts.int.padStart(maxIntLength, "0");
  const newInt = newParts.int.padStart(maxIntLength, "0");

  const oldDec = oldParts.dec;
  const newDec = newParts.dec;

  // smooth + minimum roll
  const getSequence = (start, end) => {
    if (start === end) return [start];

    const forward = (end - start + 10) % 10;
    const backward = (start - end + 10) % 10;

    let steps, direction;

    if (forward <= backward) {
      steps = forward;
      direction = 1;
    } else {
      steps = backward;
      direction = -1;
    }

    // force at least 1 full loop for visibility
    if (steps < 3) steps += 10;

    const seq = [];
    let current = start;

    for (let i = 0; i <= steps; i++) {
      seq.push(current);
      current = (current + direction + 10) % 10;
    }

    return seq;
  };

  const renderDigit = (oldDigit, newDigit, index, offset = 0) => {
    const sequence = animate
      ? getSequence(oldDigit, newDigit)
      : [oldDigit];

    return (
      <span className="digit-container" key={index}>
        <span
          className="digit-strip"
          style={{
            transform: `translateY(-${
              (sequence.length - 1) * DIGIT_HEIGHT
            }px)`,
            transition: animate
              ? `transform ${
                  600 + sequence.length * 80
                }ms cubic-bezier(0.22, 1, 0.36, 1) ${
                  (index + offset) * 80
                }ms`
              : "none",
          }}
        >
          {sequence.map((num, i) => (
            <span key={i} className="digit">
              {num}
            </span>
          ))}
        </span>
      </span>
    );
  };

  return (
    <div className="rate-roll" ref={containerRef}>
      {prefix && <span className="affix">{prefix}</span>}
      {" "}

      {/* INTEGER */}
      {newInt.split("").map((char, i) =>
        renderDigit(Number(oldInt[i]), Number(char), i)
      )}

      {/* DOT + DECIMAL */}
      <span className="decimal-group ">
        <span className="decimal">.</span>

        {newDec.split("").map((char, i) =>
          renderDigit(
            Number(oldDec[i]),
            Number(char),
            i,
            newInt.length
          )
        )}
      </span>

      {suffix && <span className="affix">{suffix}</span>}
    </div>
  );
};

export default RollingRate;